import type { Tone } from '../../ui/v2';

export const ADMIN_ORDERS_PAGE_SIZE = 25;

/** The value is sent as `status`; the endpoint omits `All`. */
export const ADMIN_ORDER_STATUS_OPTIONS = [
  { value: 'All', label: 'All Statuses' },
  { value: 'paid', label: 'Paid' },
  { value: 'pending', label: 'Pending' },
  { value: 'failed', label: 'Failed' },
] as const;

export interface AdminOrdersPage {
  orders: any[];
  /** Absent when the body is not a list: the previous count stays. */
  count?: number;
  /** Absent when the body is not a list: the previous page total stays. */
  totalPages?: number;
}

export const readAdminOrdersPage = (data: any): AdminOrdersPage => {
  if (data?.results && Array.isArray(data.results)) {
    return {
      orders: data.results,
      count: data.count || 0,
      totalPages:
        data.total_pages ||
        Math.ceil((data.count || 0) / ADMIN_ORDERS_PAGE_SIZE),
    };
  }
  if (Array.isArray(data)) {
    return { orders: data, count: data.length, totalPages: 1 };
  }
  return { orders: [] };
};

export const adminOrdersCountLabel = (count: number) =>
  `${count} order${count !== 1 ? 's' : ''}`;

export const formatAmountByCurrency = (
  cents: number | null | undefined,
  currency?: string | null,
) => {
  const code = (currency || 'USD').toUpperCase();
  try {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: code,
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format((cents ?? 0) / 100);
  } catch {
    return `${code} ${((cents ?? 0) / 100).toFixed(2)}`;
  }
};

const DATE_TIME_FORMAT: Intl.DateTimeFormatOptions = {
  month: 'short',
  day: 'numeric',
  hour: '2-digit',
  minute: '2-digit',
};

export const formatLastWebhook = (lastWebhook: unknown): string => {
  if (!lastWebhook) return '—';
  if (typeof lastWebhook === 'string' || typeof lastWebhook === 'number') {
    return String(lastWebhook);
  }
  if (typeof lastWebhook !== 'object') return '—';

  const webhook = lastWebhook as {
    event_type?: unknown;
    status?: unknown;
    received_at?: unknown;
    id?: unknown;
  };
  const parts = [
    webhook.event_type,
    webhook.status,
    webhook.received_at
      ? new Date(String(webhook.received_at)).toLocaleDateString(
          'en-US',
          DATE_TIME_FORMAT,
        )
      : null,
    webhook.id ? `#${String(webhook.id)}` : null,
  ].filter((part): part is string | number => Boolean(part));

  return parts.length ? parts.map(String).join(' · ') : '—';
};

export const formatOrderCreatedAt = (createdAt?: string | null) =>
  createdAt
    ? new Date(createdAt).toLocaleDateString('en-US', DATE_TIME_FORMAT)
    : '—';

const CURRENCY_FLAGS: Record<string, string> = {
  USD: '🇺🇸',
  CAD: '🇨🇦',
  EUR: '🇪🇺',
  GBP: '🇬🇧',
  AUD: '🇦🇺',
  JPY: '🇯🇵',
  MXN: '🇲🇽',
  BRL: '🇧🇷',
  INR: '🇮🇳',
  CNY: '🇨🇳',
  KRW: '🇰🇷',
  CHF: '🇨🇭',
  SEK: '🇸🇪',
  NZD: '🇳🇿',
  SGD: '🇸🇬',
  HKD: '🇭🇰',
  ILS: '🇮🇱',
  AED: '🇦🇪',
  PHP: '🇵🇭',
  THB: '🇹🇭',
  ZAR: '🇿🇦',
  NOK: '🇳🇴',
  DKK: '🇩🇰',
  PLN: '🇵🇱',
  COP: '🇨🇴',
  ARS: '🇦🇷',
  CLP: '🇨🇱',
  PEN: '🇵🇪',
};

/** Upper-cased code (empty when missing) and its flag (empty when unknown). */
export const orderCurrency = (currency?: string | null) => {
  const code = (currency || '').toUpperCase();
  return { code, flag: CURRENCY_FLAGS[code] || '' };
};

export const orderItemsLabel = (order: any) => {
  const itemCount = order.order_items?.length || 0;
  return `${itemCount} item${itemCount !== 1 ? 's' : ''}`;
};

export const orderStoreLabel = (order: any) =>
  order.store_name || order.store_id || order.store || '—';

export const orderPosLabel = (order: any) =>
  order.pos_integration
    ? typeof order.pos_integration === 'object'
      ? order.pos_integration.provider || '—'
      : String(order.pos_integration)
    : '—';

export const orderPaymentIdLabel = (order: any) =>
  order.stripe_payment_intent_id || order.payment_id || '—';

/** First letter upper-cased, the rest as sent; null when there is no status. */
export const orderStatusLabel = (status?: string | null) =>
  status ? status.charAt(0).toUpperCase() + status.slice(1) : null;

/** Case-insensitive; anything unlisted reads as neutral. */
export const adminOrderStatusTone = (status?: string | null): Tone => {
  const s = (status || '').toLowerCase();
  if (s === 'completed' || s === 'paid') return 'success';
  if (s === 'cancelled' || s === 'refunded') return 'danger';
  if (s === 'accepted') return 'accent';
  if (s === 'pending') return 'warning';
  return 'neutral';
};
